/* eslint-disable no-bitwise */
import './ComplianceCategorySelector.scss';

import { ComplianceCategories } from '@ui/icons';
import { enumNumericValues } from '@sonar-software/sonar-ts-lib';
import { SonarSelector } from '../SonarSelector';

import { ComplianceCategory, patchSalesforceSonarObject } from '@sonar-software/sonar-core';
import {
	genericProcessingFailure,
	useOptimisticUpdate,
	usePrevious,
	useProcessing,
	useSonarObjectReference,
	useUpdateSonarObjectReference
} from '@hooks';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const enumOptions = enumNumericValues(ComplianceCategory)
	.filter((category: ComplianceCategory) => category !== ComplianceCategory.Any);

function getValues(value: number): Array<number> {
	const results = [];

	for (const option of enumOptions) {
		if (value & option) {
			results.push(option);
		}
	}

	return results.length ? results : [ 0 ];
}

function getRawValue(values: Array<number>): number {
	return values.reduce((prev, curr) => curr + prev, 0);
}

async function updateComplianceCategory(sonarObjectId: number, complianceCategory: number) {
	const result = await patchSalesforceSonarObject({
		complianceCategory,
		sonarObjectId
	});

	return result.data ?? genericProcessingFailure;
}

export interface ComplianceCategorySelectorProps {
	direction?: 'horizontal' | 'vertical';
	getPopupContainer?: (trigger: HTMLElement) => HTMLElement;
	id?: string;
	isSpreadsheet?: boolean;
	sonarObjectId: number;
}

export const ComplianceCategorySelector: React.FC<ComplianceCategorySelectorProps> = ({
	direction = 'vertical',
	isSpreadsheet,
	sonarObjectId,
	...rest
}) => {
	const { complianceCategory } = useSonarObjectReference(sonarObjectId)?.salesforce ?? {};
	const { update } = useUpdateSonarObjectReference();
	const { start, state, stop } = useProcessing();
	const { changes } = useOptimisticUpdate(sonarObjectId);
	const previousComplianceCategory = usePrevious(complianceCategory, complianceCategory);

	const splitCategories = getValues(complianceCategory ?? ComplianceCategory.None);

	const [ value, setValue ] = useState(splitCategories);
	const originalValRef = useRef(value);
	const workingValRef = useRef(value);

	// Attempting to build this outside of the component results in compilation issues.
	const options = useMemo(() => enumOptions
		.map((category: ComplianceCategory) => ({
			label: <ComplianceCategories categoryFlags={ category } />,
			optionLabel: <ComplianceCategories categoryFlags={ category } />,
			value: category
		})), []);

	useEffect(() => {
		if (previousComplianceCategory !== complianceCategory &&
			complianceCategory !== getRawValue(value)) {
			setValue(complianceCategory ? getValues(complianceCategory) : []);
		}
	}, [ complianceCategory, previousComplianceCategory, value ]);

	const handleBlur = useCallback(async() => {
		const mergedOriginal = getRawValue(originalValRef.current);
		const mergedNew = getRawValue(workingValRef.current);

		if (mergedNew === mergedOriginal) {
			return;
		}

		const fallback = originalValRef.current;

		start();
		const result = await updateComplianceCategory(sonarObjectId, mergedNew);

		if (result.success) {
			originalValRef.current = workingValRef.current;

			update({
				salesforce: { complianceCategory: mergedNew },
				sonarObjectId
			});
		} else {
			setValue(fallback);
		}

		stop(result);
	}, [ sonarObjectId, start, stop, update ]);

	const handleClear = useCallback(() => {
		workingValRef.current = [ ComplianceCategory.None ];
		setValue(workingValRef.current);
		handleBlur();
	}, [ handleBlur ]);

	const handleChange = useCallback((change: Array<ComplianceCategory>) => {
		let newValue: Array<ComplianceCategory>;

		const changeIncludesNone = change.includes(ComplianceCategory.None);
		const valueIncludesNone = value.includes(ComplianceCategory.None);
		const changeHasOthers = change.some((changeValue) => changeValue !== ComplianceCategory.None);

		if ((changeIncludesNone && !valueIncludesNone) || !change.length) {
			newValue = [ ComplianceCategory.None ];
		} else if (valueIncludesNone && changeHasOthers) {
			newValue = change.filter((changeValue) => changeValue !== ComplianceCategory.None);
		} else {
			newValue = change;
		}

		workingValRef.current = newValue;
		setValue(newValue);
	}, [ value ]);

	return (
		<SonarSelector
			{ ...rest }
			allowClear={ !value.includes(ComplianceCategory.None) }
			boxless={ !isSpreadsheet }
			className='salesforce-compliance-category-selector'
			defaultValue={ splitCategories || [ ComplianceCategory.None ] }
			direction={ direction }
			isSalesforce
			isSpreadsheet={ isSpreadsheet }
			mode='multiple'
			onBlur={ handleBlur }
			onChange={ handleChange }
			onClear={ handleClear }
			options={ options }
			placeholder={ <ComplianceCategories categoryFlags={ ComplianceCategory.None } /> }
			popupMatchSelectWidth={ false }
			readOnlyDisplay={
				<ComplianceCategories categoryFlags={ complianceCategory || ComplianceCategory.None } />
			}
			showArrow
			showSearch={ false }
			state={ changes.complianceCategory ? { isProcessing: true } : state }
			value={ value } />
	);
};
